import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import Modal from "../common/Modal";
import { getEventById } from "../../services/eventService";
import { updateEvent } from "../../services/createEventService";
import { color } from "../../styles/theme";

/**
 * @Room Description editor for the Settings page — the same field
 * RoomHomeHeader.jsx already edits inline on the Room's own page, surfaced
 * here too as its own settings row per the QA ticket. Round-trips the full
 * current event (fetched fresh right before saving) rather than sending a
 * minimal { description } body — PutEventAsync assigns a couple of fields
 * (IsWatermark, IsJoinRequestDisabled) with no fallback if omitted, so a
 * partial payload silently resets them (same bug already fixed in
 * RoomHomeHeader.jsx's own save).
 */
const RoomDescriptionModal = ({ eventId, initialDescription, onClose, onUpdated }) => {
  const { t } = useTranslation(["rooms", "common"]);
  const [description, setDescription] = useState(initialDescription || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await getEventById(eventId);
      const currentEvent = res.data?.Result || res.data;
      await updateEvent(eventId, { ...currentEvent, Description: description });
      toast.success(t("roomSettings.descriptionModal.updateSuccess"));
      onUpdated(description);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomSettings.descriptionModal.updateError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("roomSettings.descriptionModal.title")}
      onClose={onClose}
      busy={saving}
      maxWidth={440}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving}>{saving ? t("roomSettings.descriptionModal.saving") : t("common:actions.save")}</Button>
        </>
      }
    >
      <label htmlFor="room-description-textarea" style={s.label}>{t("roomSettings.descriptionModal.label")}</label>
      <textarea
        id="room-description-textarea"
        className="input"
        style={s.textarea}
        rows={5}
        maxLength={1000}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={t("roomSettings.descriptionModal.placeholder")}
        autoFocus
      />
      <div style={s.charCount}>{description.length}/1000</div>
    </Modal>
  );
};

const s = {
  label: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  textarea: { width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  charCount: { textAlign: "right", fontSize: 11, color: color.textMuted, marginTop: 4 },
};

export default RoomDescriptionModal;
